import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import {
  fetchFailedDeliveries,
  retryFailedDelivery,
  type FailedDelivery,
} from '@/features/superadmin/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const CHANNEL_LABELS = { email: 'メール', slack: 'Slack', chatwork: 'Chatwork' } as const;

/**
 * ジョブ・障害対応（指示書 §13 仮決め5・§12 M11 / DECISIONS D-046）。
 * 通知配信の失敗のみを対象とする最小構成。定期ジョブ・移行ジョブの再実行はスコープ外。
 */
export function SuperAdminJobsPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['superadmin-failed-deliveries'],
    queryFn: fetchFailedDeliveries,
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onRetry(delivery: FailedDelivery): Promise<void> {
    setBusyId(delivery.id);
    setError(null);
    try {
      await retryFailedDelivery(delivery.id);
      setNotice(`${CHANNEL_LABELS[delivery.channel]}宛の配信を再実行しました。`);
      await queryClient.invalidateQueries({ queryKey: ['superadmin-failed-deliveries'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '再実行に失敗しました。');
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<FailedDelivery>[] = [
    { key: 'channel', header: '種別', primary: true, render: (d) => CHANNEL_LABELS[d.channel] },
    { key: 'destination', header: '宛先', render: (d) => d.destination ?? '—' },
    { key: 'retry_count', header: '再試行回数', render: (d) => String(d.retry_count) },
    { key: 'last_error', header: '最終エラー', render: (d) => d.last_error ?? '—' },
    {
      key: 'created_at',
      header: '発生日時',
      render: (d) => formatDateTime(d.created_at),
    },
  ];

  return (
    <>
      <PageHeader
        title="ジョブ・障害対応"
        description="配信に失敗した通知の一覧です。個別に再実行できます。"
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <ListTable<FailedDelivery>
        caption="配信失敗一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(d) => d.id}
        state={state}
        onRetry={() => void query.refetch()}
        rowActions={[
          {
            label: '再実行',
            onSelect: (d) => busyId === null && void onRetry(d),
          },
        ]}
        emptyTitle="失敗した配信はありません"
      />
    </>
  );
}
